import { Editor, Element as SlateElement, type Location } from 'slate';
import { type CSSProperties } from 'react';
import { isCommonBlockElement } from '../../types/guards';

const DEFAULT_ALIGNMENT = 'left';

const ALIGNMENT_MAP = {
  left: 'align-left',
  center: 'align-center',
  right: 'align-right',
  justify: 'align-justify',
} as const;

type AlignmentKeys = keyof typeof ALIGNMENT_MAP;
type AlignmentClass = (typeof ALIGNMENT_MAP)[AlignmentKeys];

class AlignmentUtils {
  static getClosestBlockAncestor(editor: Editor, location: Location) {
    return Editor.above(editor, {
      at: location,
      mode: 'lowest',
      match: (n) => SlateElement.isElement(n) && Editor.isBlock(editor, n),
    });
  }

  static currentAlignment(editor: Editor) {
    if (editor.selection) {
      const result = AlignmentUtils.getClosestBlockAncestor(
        editor,
        editor.selection.focus
      );

      if (result && isCommonBlockElement(result[0])) {
        return result[0].alignment;
      }
    }

    return DEFAULT_ALIGNMENT;
  }

  static alignmentDataFromHtmlElement(el: HTMLElement) {
    let alignment: string | undefined;
    if (el.style.textAlign) {
      alignment = el.style.textAlign;
    } else {
      for (const [key, value] of Object.entries(ALIGNMENT_MAP)) {
        if (el.classList.contains(value)) {
          alignment = key;
          break;
        }
      }
    }
    return alignment;
  }

  static alignmentStyleForElement(element: SlateElement) {
    const style: CSSProperties = {};
    if (isCommonBlockElement(element) && element.alignment) {
      style.textAlign = element.alignment;
    }
    return style;
  }

  static alignmentClassForValue(
    alignment: string | undefined
  ): AlignmentClass | undefined {
    if (alignment) {
      return ALIGNMENT_MAP[alignment];
    }
  }
}

export default AlignmentUtils;
